import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemButton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChevronDown,
  CircleDollarSign,
  Hash,
  MapPin,
  Search,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { PropertySearchesCardPropertySearchesFragment } from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { formatRange } from '@modules/_shared/utils/formatting';
import { PropertySearchDrawer } from '@modules/property-searches/PropertySearchDrawer';
import { type Translate, useLocale } from '@shared/hooks/locale';

export const PROPERTY_SEARCHES_CARD_PROPERTY_SEARCHES_FRAGMENT =
  gql(/* GraphQL */ `
    fragment PropertySearchesCardPropertySearches on property_searches {
      id
      created_at
      updated_at
      offer_type
      number_of_rooms_gte
      number_of_rooms_lte
      sale_price_lte
      sale_price_gte
      monthly_rent_lte
      monthly_rent_gte
      property_searches_places {
        place {
          id
          name
          label
        }
      }
    }
  `);

type PropertySearchesCardProps = {
  propertySearches: PropertySearchesCardPropertySearchesFragment[];
  prefilledUserId?: string;
  defaultExpanded?: boolean;
  condensed?: boolean;
  readOnly?: boolean;
  variant?: 'default' | 'legacy';
  subtitle?: React.ReactNode;
  onCreate?: (id: string) => void;
  onUpdate?: (id: string) => void;
  onDelete?: (id: string) => void;
  slotProps?: {
    title?: {
      sx?: SxProps<Theme>;
    };
    subtitle?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const PropertySearchesCard: React.FC<PropertySearchesCardProps> = ({
  defaultExpanded = false,
  propertySearches,
  onCreate,
  onUpdate,
  onDelete,
  prefilledUserId,
  condensed = false,
  readOnly = false,
  variant = 'default',
  subtitle,
  slotProps,
}) => {
  const { t } = useLocale();
  const { text, zIndex } = useTheme();
  const isLegacy = variant === 'legacy';
  const prevDefaultExpanded = useRef(defaultExpanded);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const [openNewPropertySearchDrawer, setOpenNewPropertySearchDrawer] =
    useState(false);

  const toggleCardAccordion = useCallback(
    (_event: React.ChangeEvent<{}>, expanded: boolean) => {
      setIsExpanded(expanded);
    },
    [],
  );

  useEffect(() => {
    if (prevDefaultExpanded.current !== defaultExpanded) {
      prevDefaultExpanded.current = defaultExpanded;
      setIsExpanded(defaultExpanded);
    }
  }, [defaultExpanded]);

  const title = (
    <>
      {t('Property searches')}
      {propertySearches.length > 0 && (
        <CountBadge count={propertySearches.length} />
      )}
    </>
  );

  const listContent = (
    <List dense disablePadding>
      {propertySearches.map(propertySearch => (
        <Fragment key={propertySearch.id}>
          <PropertySearchItem
            propertySearch={propertySearch}
            onItemClicked={setSelectedItemId}
            condensed={condensed}
            readOnly={readOnly}
          />
          <Divider component="li" />
        </Fragment>
      ))}
      {onCreate && (
        <ListItem
          sx={{
            py: isLegacy ? 0 : 1.5,
            pt: isLegacy && propertySearches.length > 0 ? 2 : undefined,
          }}
        >
          <Button
            variant={isLegacy ? 'contained' : 'soft'}
            startIcon={<Search size={isLegacy ? 16 : 14} />}
            onClick={() => setOpenNewPropertySearchDrawer(true)}
            fullWidth
          >
            {t('Add Property Search')}
          </Button>
        </ListItem>
      )}
    </List>
  );

  const accordionContent = isLegacy ? (
    <Accordion expanded={isExpanded} onChange={toggleCardAccordion}>
      <AccordionSummary
        expandIcon={<ChevronDown size={18} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Typography
          variant="subtitle2"
          component="span"
          sx={slotProps?.title?.sx}
        >
          {title}
        </Typography>
        {subtitle && (
          <Fade in={!isExpanded}>
            <Typography
              variant="caption"
              component="span"
              sx={{
                ...text.ellipsis,
                ...(slotProps?.subtitle?.sx ?? {}),
              }}
            >
              {subtitle}
            </Typography>
          </Fade>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 2 }}>{listContent}</AccordionDetails>
    </Accordion>
  ) : (
    <ListingAccordion
      title={title}
      expanded={isExpanded}
      onChange={toggleCardAccordion}
      disablePadding
    >
      {listContent}
    </ListingAccordion>
  );

  return (
    <>
      {accordionContent}
      {!readOnly && selectedItemId && (
        <PropertySearchDrawer
          propertySearchId={selectedItemId}
          onClose={() => setSelectedItemId(null)}
          slotProps={{ drawer: { sx: { zIndex: zIndex.drawer + 2 } } }}
          onUpsert={onUpdate}
          onDelete={
            onDelete
              ? () => {
                  setSelectedItemId(null);
                  onDelete(selectedItemId);
                }
              : undefined
          }
        />
      )}
      {onCreate && openNewPropertySearchDrawer && (
        <PropertySearchDrawer
          selectedContact={prefilledUserId}
          onClose={() => setOpenNewPropertySearchDrawer(false)}
          onUpsert={id => {
            setOpenNewPropertySearchDrawer(false);
            onCreate(id);
          }}
          slotProps={{ drawer: { sx: { zIndex: zIndex.drawer + 2 } } }}
        />
      )}
    </>
  );
};

const offerTypeToLabel = (offerType: string | null, t: Translate) => {
  switch (offerType) {
    case 'buy':
      return t('Buy');
    case 'rent':
      return t('Rent');
    default:
      return t('Unknown offer type');
  }
};

const SearchChip: React.FC<{
  icon: React.ReactNode;
  condensed: boolean;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
}> = ({ icon, condensed, sx, children }) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, ...sx }}>
    {!condensed && icon}
    {children}
  </Stack>
);

const PropertySearchItem: React.FC<{
  propertySearch: PropertySearchesCardPropertySearchesFragment;
  onItemClicked: (propertySearchId: string) => void;
  condensed?: boolean;
  readOnly?: boolean;
}> = ({
  propertySearch,
  onItemClicked,
  condensed = false,
  readOnly = false,
}) => {
  const { t, locale } = useLocale();
  const primary = offerTypeToLabel(propertySearch.offer_type ?? null, t);

  const priceLabel =
    propertySearch.offer_type === 'buy'
      ? !!propertySearch.sale_price_lte || !!propertySearch.sale_price_gte
        ? formatRange(
            propertySearch.sale_price_gte,
            propertySearch.sale_price_lte,
            '',
            locale,
          )
        : null
      : !!propertySearch.monthly_rent_lte || !!propertySearch.monthly_rent_gte
      ? formatRange(
          propertySearch.monthly_rent_gte,
          propertySearch.monthly_rent_lte,
          ` / ${t('month')}`,
          locale,
        )
      : null;

  const roomsLabel = formatRange(
    propertySearch.number_of_rooms_gte,
    propertySearch.number_of_rooms_lte,
    '',
    locale,
  );

  const places = propertySearch.property_searches_places ?? [];
  const firstPlace = places[0]?.place;
  const firstPlaceLabel = firstPlace?.label ?? firstPlace?.name ?? null;
  const extraPlacesCount = Math.max(places.length - 1, 0);

  return (
    <ListItemButton
      disabled={readOnly}
      onClick={() => onItemClicked(propertySearch.id)}
      sx={{
        py: 1.25,
        px: 2,
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              bgcolor:
                propertySearch.offer_type === 'buy'
                  ? 'primary.lighter'
                  : 'warning.lighter',
              color:
                propertySearch.offer_type === 'buy'
                  ? 'primary.main'
                  : 'warning.dark',
              fontSize: '0.75rem',
              fontWeight: 600,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            {primary}
          </Box>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              minWidth: 0,
            }}
          >
            {firstPlaceLabel != null && (
              <SearchChip
                icon={
                  <MapPin size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
                }
                condensed={condensed}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: 'text.secondary' }}
                >
                  {firstPlaceLabel}
                </Typography>
                {extraPlacesCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}
                  >
                    +{extraPlacesCount}
                  </Typography>
                )}
              </SearchChip>
            )}
            {roomsLabel && (
              <SearchChip
                icon={
                  <Hash size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
                }
                condensed={condensed}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  {roomsLabel} {t('Rooms').toLowerCase()}
                </Typography>
              </SearchChip>
            )}
            {priceLabel && (
              <SearchChip
                icon={
                  <CircleDollarSign
                    size={12}
                    style={{ opacity: 0.5, flexShrink: 0 }}
                  />
                }
                condensed={condensed}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  {priceLabel}
                </Typography>
              </SearchChip>
            )}
          </Stack>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', whiteSpace: 'nowrap', ml: 1 }}
        >
          <TimeAgo
            dateString={propertySearch.updated_at ?? propertySearch.created_at}
          />
        </Typography>
      </Stack>
    </ListItemButton>
  );
};
